import React from "react";
import { ESidebarDirection, ESidebarModes } from "@ddm-design-system/sidebar";
import {
  Body,
  BodyHighlight,
  Description,
  DescriptionMedium,
  SectionTitle,
  Subtitle
} from "@ddm-design-system/typography";
import { Icon, IconNames } from "@ddm-design-system/icon";
import { Divider } from "@ddm-design-system/divider";
import { IX10, X10Level, X10LimitState } from "../../../store/installation/types";
import {
  X10_KEG_TIME_LIMIT_DAYS,
  X10_RECOMMENDED_MAX_TEMPERATURE,
  X10_RECOMMENDED_MIN_TEMPERATURE
} from "../../../constants";
import { StyledSideBar } from "../PressureChamberDetailsSidebar";
import { ReactComponent as X10Icon } from "../../../assets/x10/x10.svg";
import frontX10 from "../../../assets/x10/front_x10.png";
import openTowerX10 from "../../../assets/x10/open_tower.png";
import X10Keg from "./X10Keg";
import useX10Text from "./useX10Text";
import {
  formatLitres,
  formatTemperature,
  formatX10Date,
  x10BeverageLabel,
  x10HardwareParts,
  x10HeadlineText
} from "./x10Presentation";

interface IProps {
  x10?: IX10;
  isVisible: boolean;
  handleClose: () => void;
}

const BANNER: { [level in X10Level]: { className: string; icon: IconNames } } = {
  ok: { className: "bg-success-success25 text-black", icon: "Ok" },
  warning: { className: "bg-warning-warning100 text-black", icon: "Warning" },
  danger: { className: "bg-alert-alert100 text-white", icon: "Warning" },
  offline: { className: "bg-grey-grey100 text-white", icon: "Offline" }
};

const TILE: { [state in X10LimitState]: string } = {
  ok: "border-grey-greenish",
  warning: "border-warning-warning100 bg-warning-warning25",
  danger: "border-alert-alert100 bg-alert-alert25"
};

// 0–12 °C scale for the temperature gauge; the recommended band is 0–5 °C
const GAUGE_MAX = 12;
const gaugePosition = (value: number) => Math.max(0, Math.min(100, (value / GAUGE_MAX) * 100));

const Tile: React.FC<{ icon: IconNames; title: string; state?: X10LimitState; wide?: boolean }> = ({
  icon,
  title,
  state = "ok",
  wide,
  children
}) => (
  <div
    className={`flex flex-col gap-xxs box-border p-md rounded-10 border-2 text-left ${
      TILE[state]
    } ${wide ? "col-span-2" : ""}`}
    data-test-id="x10-tile"
  >
    <span className="flex items-center gap-xs text-grey-grey100">
      <Icon name={icon} size={16} className="fill-current" />
      <Description>{title}</Description>
    </span>
    {children}
  </div>
);

const DetailRow: React.FC<{ label: string }> = ({ label, children }) => (
  <div className="flex justify-between items-center gap-md w-full py-xs">
    <Description className="text-grey-grey100 text-left">{label}</Description>
    <div className="flex items-center gap-xs text-right">{children}</div>
  </div>
);

export const X10DetailsSidebar: React.FC<IProps> = ({ x10, isVisible, handleClose }) => {
  const t = useX10Text();

  return (
    <StyledSideBar
      className="!fixed overflow-auto max-h-screen !w-[432px] xs:!w-full xs:mt-xxxl xs:rounded-t-10 xs:!h-[calc(100%-64px)]"
      open={isVisible}
      onClose={handleClose}
      mode={ESidebarModes.OVER}
      direction={ESidebarDirection.RIGHT}
    >
      {isVisible && x10 ? <X10Details x10={x10} t={t} handleClose={handleClose} /> : <></>}
    </StyledSideBar>
  );
};

const X10Details: React.FC<{
  x10: IX10;
  t: ReturnType<typeof useX10Text>;
  handleClose: () => void;
}> = ({ x10, t, handleClose }) => {
  const { evaluation } = x10;
  const banner = BANNER[x10.status];
  const headline = x10HeadlineText(x10, t);
  const active = !x10.offline && !evaluation.open;
  const hardwareParts = x10HardwareParts(x10, t);
  const temperatureState: X10LimitState = evaluation.highTemperature
    ? "danger"
    : evaluation.coolingDown
    ? "warning"
    : "ok";

  return (
    <div
      className="flex flex-col items-center text-center pb-xxl relative"
      data-test-id="x10-sidebar"
    >
      <div className="flex items-center justify-between gap-md sticky box-border bg-white p-lg top-0 w-full border-b border-grey-grey50 z-[1]">
        <div className="flex items-center gap-sm min-w-0">
          <X10Icon className="flex-shrink-0 w-[14px] h-[28px] text-black" aria-hidden="true" />
          <div className="flex flex-col items-start min-w-0">
            <Subtitle className="truncate">{x10.name}</Subtitle>
            {x10.serialNumber && (
              <Description className="text-grey-grey100">{x10.serialNumber}</Description>
            )}
          </div>
        </div>
        <Icon
          name="Close"
          className="cursor-pointer flex-shrink-0"
          size={16}
          onClick={handleClose}
        />
      </div>

      <div
        className={`flex flex-col items-center box-border w-full p-lg ${banner.className}`}
        data-test-id="x10-banner"
      >
        <div className="flex items-center justify-center">
          <Icon className="mr-xs fill-current" name={banner.icon} size={24} />
          <BodyHighlight>{headline.title}</BodyHighlight>
        </div>
        <Description className="mt-sm">{headline.description}</Description>
      </div>

      <div className="flex items-center justify-center gap-xl box-border w-full px-lg pt-xl">
        <div className="flex items-center justify-center w-[180px] h-[220px]">
          <img
            src={evaluation.open ? openTowerX10 : frontX10}
            alt={t("machine")}
            className={`max-h-full max-w-full object-contain ${
              evaluation.open ? "w-[180px] h-auto" : "h-[210px] w-auto"
            }`}
            // inline: the app's global .filter class (common/filter) sets width: 100%
            style={x10.offline ? { filter: "grayscale(1)", opacity: 0.5 } : undefined}
          />
        </div>
        <div className="transform-13">
          <X10Keg x10={x10} />
        </div>
      </div>
      <div className="flex flex-col items-center gap-xxs px-lg pt-lg">
        <SectionTitle>{x10BeverageLabel(x10, t)}</SectionTitle>
        {x10.hasKeg && !x10.offline && (
          <Description className="text-grey-grey100">
            {t("volume_of", {
              LEFT: formatLitres(x10.volumeLeft),
              TOTAL: formatLitres(x10.volume)
            })}
          </Description>
        )}
      </div>

      {!x10.offline && (
        <>
          <Divider className="my-xl !bg-grey-grey25" />
          <div className="grid grid-cols-2 gap-md box-border w-full px-lg">
            {active && x10.hasKeg && evaluation.daysOnKeg !== undefined && (
              <Tile icon="Sleep" title={t("time_on_keg")} state={evaluation.kegTime}>
                <BodyHighlight>
                  {t("time_on_keg_value", {
                    DAYS: evaluation.daysOnKeg,
                    LIMIT: X10_KEG_TIME_LIMIT_DAYS
                  })}
                </BodyHighlight>
                <div className="relative h-[6px] w-full rounded-10 bg-grey-greenish overflow-hidden">
                  <div
                    className={`absolute left-0 top-0 bottom-0 ${
                      evaluation.kegTime === "danger"
                        ? "bg-alert-alert100"
                        : evaluation.kegTime === "warning"
                        ? "bg-warning-warning100"
                        : "bg-success-success100"
                    }`}
                    style={{
                      width: `${Math.min(
                        100,
                        (evaluation.daysOnKeg / X10_KEG_TIME_LIMIT_DAYS) * 100
                      )}%`
                    }}
                  />
                </div>
                <Description>
                  {t(`time_on_keg_${evaluation.kegTime || "ok"}`, {
                    DAYS: Math.abs(X10_KEG_TIME_LIMIT_DAYS - evaluation.daysOnKeg)
                  })}
                </Description>
              </Tile>
            )}
            {active && x10.hasKeg && (
              <>
                {evaluation.daysToExpiry !== undefined ? (
                  <Tile
                    icon="CalendarCircle"
                    title={t("best_before")}
                    state={evaluation.expiration}
                  >
                    <BodyHighlight>{formatX10Date(x10.bestBefore).split(",")[0]}</BodyHighlight>
                    <Description>
                      {evaluation.daysToExpiry < 0
                        ? t("best_before_ago", { DAYS: Math.abs(evaluation.daysToExpiry) })
                        : t("best_before_in", { DAYS: evaluation.daysToExpiry })}
                    </Description>
                  </Tile>
                ) : (
                  <Tile icon="NfcFill" title={t("beverage_tag")}>
                    <BodyHighlight>
                      {x10.hasNfcBeverage ? t("beverage_unknown") : t("beverage_tag_missing")}
                    </BodyHighlight>
                    <Description>
                      {x10.hasNfcBeverage
                        ? t("beverage_tag_unknown_description")
                        : t("beverage_tag_missing_description")}
                    </Description>
                  </Tile>
                )}
              </>
            )}
            {active && (
              <Tile icon="Temperature" title={t("temperature")} state={temperatureState} wide>
                <div className="flex items-end gap-xl">
                  <div className="flex flex-col items-start">
                    <Subtitle
                      className={evaluation.highTemperature ? "text-alert-alert100" : undefined}
                    >
                      {formatTemperature(x10.vesselTemperature)}
                    </Subtitle>
                    <Description className="text-grey-grey100">
                      {t("temperature_vessel")}
                    </Description>
                  </div>
                  <div className="flex flex-col items-start">
                    <SectionTitle>{formatTemperature(x10.ambientTemperature)}</SectionTitle>
                    <Description className="text-grey-grey100">{t("temperature_room")}</Description>
                  </div>
                  {x10.enhancedCoolingRunning && (
                    <DescriptionMedium className="ml-auto rounded-[40px] px-sm py-xxs bg-grey-greenish">
                      {t("temperature_boost")}
                    </DescriptionMedium>
                  )}
                </div>
                {x10.vesselTemperature !== undefined && (
                  <div className="relative w-full h-[8px] mt-md rounded-10 bg-grey-grey25">
                    <div
                      className="absolute left-0 top-0 bottom-0 rounded-10 bg-success-success50"
                      style={{ width: `${gaugePosition(X10_RECOMMENDED_MAX_TEMPERATURE)}%` }}
                    />
                    <div
                      className={`absolute top-[-5px] w-[18px] h-[18px] box-border rounded-[50%] border-[3px] border-white shadow-8 ${
                        temperatureState === "danger"
                          ? "bg-alert-alert100"
                          : temperatureState === "warning"
                          ? "bg-warning-warning100"
                          : "bg-success-success100"
                      }`}
                      style={{ left: `calc(${gaugePosition(x10.vesselTemperature)}% - 9px)` }}
                      data-test-id="x10-temperature-marker"
                    />
                  </div>
                )}
                <div className="relative w-full h-[16px] text-grey-grey100">
                  <Description className="absolute left-0">
                    {X10_RECOMMENDED_MIN_TEMPERATURE} °C
                  </Description>
                  <Description
                    className="absolute -translate-x-1/2 transform"
                    style={{ left: `${gaugePosition(X10_RECOMMENDED_MAX_TEMPERATURE)}%` }}
                  >
                    {X10_RECOMMENDED_MAX_TEMPERATURE} °C
                  </Description>
                  <Description className="absolute right-0">{GAUGE_MAX} °C</Description>
                </div>
                <Description className="mt-xs">
                  {evaluation.roomTooHot
                    ? t("temperature_room_too_hot")
                    : evaluation.highTemperature
                    ? t("temperature_high")
                    : evaluation.coolingDown
                    ? t("temperature_cooling_down")
                    : t("temperature_ok")}
                </Description>
              </Tile>
            )}
            {x10.health && (
              <Tile
                icon="FaultySensor"
                title={t("hardware")}
                state={evaluation.hardwareError ? "danger" : "ok"}
                wide
              >
                {hardwareParts.map(part => (
                  <div key={part.key} className="flex items-center gap-sm w-full py-xxs">
                    <Icon
                      name={part.fault ? "Warning" : "Ok"}
                      size={16}
                      className={`fill-current ${
                        part.fault ? "text-alert-alert100" : "text-success-success100"
                      }`}
                    />
                    <Body className="flex-1 text-left">{part.label}</Body>
                    <DescriptionMedium
                      className={part.fault ? "text-alert-alert100" : "text-success-success100"}
                    >
                      {part.fault ? t("hardware_fault") : t("hardware_ok")}
                    </DescriptionMedium>
                  </div>
                ))}
              </Tile>
            )}
          </div>
        </>
      )}

      <Divider className="my-xl !bg-grey-grey25" />
      <div className="flex flex-col box-border w-full px-lg" data-test-id="x10-details">
        <SectionTitle className="text-left mb-sm">{t("details")}</SectionTitle>
        {x10.serialNumber && (
          <DetailRow label={t("serial_number")}>
            <Body>{x10.serialNumber}</Body>
          </DetailRow>
        )}
        {x10.firmware && (
          <DetailRow label={t("firmware")}>
            <Body>{x10.firmware}</Body>
            <DescriptionMedium
              className={`rounded-[40px] px-sm py-xxs ${
                x10.updateAvailable ? "bg-warning-warning25" : "bg-success-success25"
              }`}
            >
              {x10.updateAvailable
                ? t("firmware_update", { VERSION: x10.targetFirmware })
                : t("firmware_up_to_date")}
            </DescriptionMedium>
          </DetailRow>
        )}
        {x10.hasKeg && (
          <DetailRow label={t("keg_mounted")}>
            <Body>{formatX10Date(x10.lastMounted)}</Body>
          </DetailRow>
        )}
        <DetailRow label={t("last_pour")}>
          <Body>{formatX10Date(x10.lastUsed)}</Body>
        </DetailRow>
        <DetailRow label={t("last_seen_label")}>
          <Body>{formatX10Date(x10.lastHeartbeat)}</Body>
        </DetailRow>
        {x10.createdAt && (
          <DetailRow label={t("installed")}>
            <Body>{formatX10Date(x10.createdAt).split(",")[0]}</Body>
          </DetailRow>
        )}
        <Description className="text-grey-grey100 text-left mt-md">{t("read_only")}</Description>
      </div>
    </div>
  );
};

export default X10DetailsSidebar;
